import { useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

interface UsePaginationStateReturn {
  page: number;
  limit: number;
  offset: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  resetToFirstPage: () => void;
}

/**
 * Manages pagination state in URL search params.
 *
 * URL is the source of truth - state is derived from searchParams.
 * Uses { replace: true } for page changes to avoid history pollution.
 *
 * @param defaultLimit - Default items per page (default: 50)
 */
export function usePaginationState(defaultLimit: number = 50): UsePaginationStateReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive state from URL (URL is source of truth)
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
  const limit = Math.max(1, Math.min(1000, parseInt(searchParams.get('limit') || String(defaultLimit), 10)));
  const offset = (page - 1) * limit;

  // Set page (with { replace: true } to avoid history pollution)
  const setPage = useCallback((newPage: number) => {
    const validPage = Math.max(1, newPage);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (validPage === 1) {
          next.delete('page'); // Clean URL for page 1
        } else {
          next.set('page', String(validPage));
        }
        return next;
      },
      { replace: true } // Don't create history entry for page changes
    );
  }, [setSearchParams]);

  // Set limit
  const setLimit = useCallback((newLimit: number) => {
    const validLimit = Math.max(1, Math.min(1000, newLimit));
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('limit', String(validLimit));
      next.delete('page'); // Reset to page 1 when changing limit
      return next;
    });
  }, [setSearchParams]);

  // Reset to first page (useful when filters change)
  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  return {
    page,
    limit,
    offset,
    setPage,
    setLimit,
    resetToFirstPage,
  };
}
